import React, { useEffect, useMemo, useState } from "react";
import EditableTable from "../components/EditableTable";
import {
  catalogRowDomId,
  isProblematicRow,
} from "../utils/catalogProduct";
import { PageLoader } from "../components/Loader";
import { posterGetProducts } from "../api/poster";
import { asArray, asPosterInner } from "../poster/response";
import {
  getCachedPosterUser,
  getPosterToken,
  setCachedPosterUser,
  setPosterToken,
} from "../session";
import { MOCK_USER } from "../mocks";

const IS_DEV = process.env.REACT_APP_DEV === "true";

function mapProductsFromResponse(products) {
  const mapped = [];
  for (const p of products) {
    if (p?.modifications && Array.isArray(p.modifications)) {
      for (const mod of p.modifications) {
        mapped.push({
          id: p.product_id + "-" + mod.modificator_id,
          name: p.product_name + " " + mod.modificator_name,
          icps:
            p.extras && p.extras[mod.modificator_id]
              ? JSON.parse(p.extras[mod.modificator_id])?.icps
              : "",
          packageCode:
            p.extras && p.extras[mod.modificator_id]
              ? JSON.parse(p.extras[mod.modificator_id])?.packageCode
              : "",
          hasMarking:
            p.extras && p.extras[mod.modificator_id]
              ? JSON.parse(p.extras[mod.modificator_id])?.hasMarking
              : false,
          packages:
            p.extras &&
            p.extras[mod.modificator_id] &&
            JSON.parse(p.extras[mod.modificator_id])?.packages
              ? JSON.parse(
                  JSON.parse(p.extras[mod.modificator_id])?.packages,
                )
              : [],
          commissionTIN:
            p.extras && p.extras[mod.modificator_id]
              ? JSON.parse(p.extras[mod.modificator_id])?.commissionTIN
              : "",
          ownerType:
            p.extras && p.extras[mod.modificator_id]
              ? JSON.parse(p.extras[mod.modificator_id])?.ownerType
              : 0,
        });
      }
    } else {
      mapped.push({
        id: p.product_id,
        name: p.product_name || "",
        icps: p?.extras?.icps || "",
        packageCode: p?.extras?.packageCode || "",
        hasMarking: Boolean(Number(p?.extras?.hasMarking)) || false,
        packages:
          p?.extras?.packages &&
          Array.isArray(JSON.parse(p.extras.packages))
            ? JSON.parse(p.extras.packages)
            : [],
        commissionTIN: p?.extras?.commissionTIN || "",
        ownerType: p?.extras?.ownerType || 0,
        modifications: [],
      });
    }
  }
  return mapped;
}

const headers = [
  { key: "id", label: "ID" },
  { key: "name", label: "Наименование" },
  { key: "icps", label: "ИКПУ" },
  { key: "packageCode", label: "Код ед. измерения" },
  { key: "hasMarking", label: "Маркировка" },
  { key: "action", label: "Действия" },
];

/**
 * @param {{ ownerInfo?: { company_name?: string } }} [props]
 */
export default function CatalogPage() {
  const [data, setTableData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [focusProblematicIndex, setFocusProblematicIndex] = useState(0);

  const u = getCachedPosterUser();
  const companyName =
    /** @type {any} */(u?.ownerInfo)?.company_name ||
    /** @type {any} */(u)?.company_name ||
    "Каталог";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      if (IS_DEV && !getPosterToken() && MOCK_USER?.access_token) {
        setPosterToken(String(MOCK_USER.access_token));
        setCachedPosterUser(MOCK_USER);
      }
      const token = getPosterToken();
      if (!token) {
        setTableData([]);
        setLoading(false);
        return;
      }
      try {
        let products;
          const raw = await posterGetProducts(token);
          const inner = asPosterInner(raw);
          products = asArray(inner);
        
        if (!cancelled) {
          setTableData(mapProductsFromResponse(products));
        }
      } catch (err) {
        console.error("Каталог:", err);
        if (!cancelled) {
          setTableData([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
        window.top?.postMessage({ hideSpinner: true }, "*");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredData = useMemo(() => {
    if (!search || search.length < 3) {
      return data;
    }
    const s = search.toLowerCase();
    return data.filter(
      (item) =>
        item.name?.toLowerCase().includes(s) ||
        item.icps?.toLowerCase().includes(s),
    );
  }, [search, data]);

  const problematicCount = useMemo(
    () => data.filter(isProblematicRow).length,
    [data],
  );

  const visibleProblematic = useMemo(
    () => filteredData.filter(isProblematicRow),
    [filteredData],
  );

  useEffect(() => {
    setFocusProblematicIndex((prev) => {
      if (visibleProblematic.length === 0) return 0;
      return Math.min(prev, visibleProblematic.length - 1);
    });
  }, [visibleProblematic]);

  const focusedRowId =
    visibleProblematic[focusProblematicIndex]?.id ?? null;

  const scrollToProblematicAtIndex = (index) => {
    const row = visibleProblematic[index];
    if (!row) return;
    const el = document.getElementById(catalogRowDomId(row.id));
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  };

  const goPrevProblematic = () => {
    if (visibleProblematic.length === 0) return;
    const nextIndex =
      (focusProblematicIndex - 1 + visibleProblematic.length) %
      visibleProblematic.length;
    setFocusProblematicIndex(nextIndex);
    queueMicrotask(() => scrollToProblematicAtIndex(nextIndex));
  };

  const goNextProblematic = () => {
    if (visibleProblematic.length === 0) return;
    const nextIndex =
      (focusProblematicIndex + 1) % visibleProblematic.length;
    setFocusProblematicIndex(nextIndex);
    queueMicrotask(() => scrollToProblematicAtIndex(nextIndex));
  };

  if (loading) {
    return (
      <section className="bodyContainer">
        <PageLoader />
      </section>
    );
  }

  if (!getPosterToken()) {
    return (
      <section className="bodyContainer">
        <h2>Нет сессии Poster</h2>
        <p className="text-muted">Откройте приложение по ссылке с OAuth-кодом в пути.</p>
      </section>
    );
  }

  return (
    <section className="bodyContainer">
      <div className="tableContainer catalogPageLayout">
        <div className="catalogPageHeaderBlock">
          <div className="tableHeader">
            <h2>{companyName}</h2>
            <h3>Товары</h3>
            <p style={{ color: "gray" }}>
              Кликните по ячейке, чтобы редактировать текст, или используйте
              переключатель.
            </p>
          </div>

          <div className="catalogProblematicBanner mb-3">
            <div className="catalogProblematicBanner__counts">
            <strong>Проблемные товары:</strong>{" "}
            <span className="catalogProblematicBanner__num">
              {problematicCount}
            </span>
            {visibleProblematic.length < problematicCount && (
              <span className="catalogProblematicBanner__sub text-muted ms-2">
                в текущем списке: {visibleProblematic.length}
              </span>
            )}
            </div>
            <div className="catalogProblematicBanner__nav">
            <button
              type="button"
              className="catalogProblematicBanner__navBtn"
              disabled={visibleProblematic.length === 0}
              onClick={goPrevProblematic}
            >
              Предыдущий
            </button>
            <button
              type="button"
              className="catalogProblematicBanner__navBtn"
              disabled={visibleProblematic.length === 0}
              onClick={goNextProblematic}
            >
              Следующий
            </button>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <input
              type="text"
              placeholder="Поиск..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border p-2 rounded w-64"
            />

            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="px-3 py-2 bg-gray-200 rounded"
              >
                Очистить
              </button>
            )}
          </div>
        </div>

        <div className="catalogPageTableScroll">
          <div className="tableBody">
            <EditableTable
              data={filteredData}
              headers={headers}
              isProblematic={isProblematicRow}
              focusedRowId={focusedRowId}
              rowDomId={(row) => catalogRowDomId(row.id)}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
